import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { useAuth } from '../hooks/useAuth';
import { authAPI } from '../lib/auth-api';
import { Billing } from '../components/billing/Billing';
import type { ApiKey, Invitation, User } from '../types';

type SettingsTab = 'profile' | 'users' | 'invitations' | 'api-keys' | 'billing';

const VALID_TABS: SettingsTab[] = ['profile', 'users', 'invitations', 'api-keys', 'billing'];

export function Settings() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as SettingsTab | null;
  const [activeTab, setActiveTab] = useState<SettingsTab>(
    tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'profile'
  );

  // Only admins and owners can see management tabs
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  return (
    <div>
      <h1 className="text-2xl font-bold text-foreground mb-6">Admin</h1>

      {/* Tabs */}
      <div className="border-b border-border mb-6 overflow-x-auto overflow-y-hidden">
        <nav className="-mb-px flex space-x-8 min-w-max">
          <button
            onClick={() => setActiveTab('profile')}
            className={`${
              activeTab === 'profile'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
          >
            Profile
          </button>
          {isAdmin && (
            <>
              <button
                onClick={() => setActiveTab('users')}
                className={`${
                  activeTab === 'users'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                Users
              </button>
              <button
                onClick={() => setActiveTab('invitations')}
                className={`${
                  activeTab === 'invitations'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                Invitations
              </button>
              <button
                onClick={() => setActiveTab('api-keys')}
                className={`${
                  activeTab === 'api-keys'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                API Keys
              </button>
              <button
                onClick={() => setActiveTab('billing')}
                className={`${
                  activeTab === 'billing'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                Billing & Plans
              </button>
            </>
          )}
        </nav>
      </div>

      {activeTab === 'profile' && <Profile />}
      {activeTab === 'users' && isAdmin && <Users onInvite={() => setActiveTab('invitations')} />}
      {activeTab === 'invitations' && isAdmin && <Invitations />}
      {activeTab === 'api-keys' && isAdmin && <APIKeys />}
      {activeTab === 'billing' && isAdmin && <Billing />}
    </div>
  );
}

function Profile() {
  const { user, customer } = useAuth();

  return (
    <div className="space-y-6">
      {/* User Profile */}
      <Card title="Profile Information">
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Email</dt>
            <dd className="mt-1 text-sm text-foreground">{user?.email}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Role</dt>
            <dd className="mt-1 text-sm text-foreground">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                {user?.role}
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Company</dt>
            <dd className="mt-1 text-sm text-foreground">{customer?.name}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Customer ID</dt>
            <dd className="mt-1 text-sm text-foreground font-mono">{customer?.id}</dd>
          </div>
        </dl>
      </Card>

      {/* About */}
      <Card title="About">
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Version</dt>
            <dd className="text-foreground font-medium">1.0.0</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Build Number</dt>
            <dd className="text-foreground font-mono">
              {import.meta.env.VITE_BUILD_NUMBER || 'dev'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Build Date</dt>
            <dd className="text-foreground">{new Date().toLocaleDateString()}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Environment</dt>
            <dd className="text-foreground capitalize">
              {import.meta.env.MODE}
            </dd>
          </div>
        </dl>
      </Card>
    </div>
  );
}

function APIKeys() {
  const [showNewKey, setShowNewKey] = useState(false);
  const [newKeyData, setNewKeyData] = useState<{ key: string; id: string } | null>(null);
  const queryClient = useQueryClient();

  const { data: keysData, isLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => authAPI.listApiKeys(),
  });

  const createMutation = useMutation({
    mutationFn: () => authAPI.createApiKey(`Key ${new Date().toLocaleString()}`),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setNewKeyData({ key: data.key, id: data.id });
      setShowNewKey(true);
    },
    onError: (err: Error) => {
      alert(`Failed to create API key: ${err.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (keyId: string) => authAPI.deleteApiKey(keyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      alert('API key deleted successfully');
    },
    onError: (err: Error) => {
      alert(`Failed to delete API key: ${err.message}`);
    },
  });

  const handleCopyKey = () => {
    if (newKeyData) {
      navigator.clipboard.writeText(newKeyData.key);
      alert('API key copied to clipboard');
    }
  };

  return (
    <div className="space-y-6">
      {/* Create API Key */}
      <Card title="Create New API Key">
        <div className="space-y-4">
          <div className="bg-info/10 border border-info/20 rounded-md p-4">
            <h6 className="text-sm font-medium text-info mb-2">Security Best Practices</h6>
            <ul className="text-sm text-info space-y-1 list-disc list-inside">
              <li>Store keys securely</li>
              <li>Rotate keys regularly</li>
              <li>Revoke unused keys immediately</li>
              <li>Never commit keys to version control</li>
            </ul>
          </div>
          <Button
            onClick={() => createMutation.mutate()}
            isLoading={createMutation.isPending}
          >
            Generate New API Key
          </Button>
        </div>
      </Card>

      {/* Show New Key Modal */}
      {showNewKey && newKeyData && (
        <Card title="New API Key Created">
          <div className="space-y-4">
            <div className="bg-warning/10 border border-warning/20 rounded-md p-4">
              <p className="text-sm text-warning font-medium">
                ⚠️ Save this key now! You won't be able to see it again.
              </p>
            </div>
            <div className="bg-muted/50 p-3 rounded-md">
              <code className="text-sm font-mono break-all">{newKeyData.key}</code>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCopyKey}>
                Copy to Clipboard
              </Button>
              <Button variant="secondary" onClick={() => setShowNewKey(false)}>
                Done
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* API Keys List */}
      <Card title="API Keys">
        {isLoading ? (
          <p className="text-center py-8 text-muted-foreground">Loading...</p>
        ) : keysData && keysData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                    Key ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                    Created By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                    Last Used
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {keysData.map((key: ApiKey) => (
                  <tr key={key.id} className="hover:bg-accent/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-foreground">
                      {key.id.slice(0, 8)}...
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                      {key.created_by_email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                      {new Date(key.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <button
                        onClick={() => {
                          if (confirm('Are you sure you want to delete this API key?')) {
                            deleteMutation.mutate(key.id);
                          }
                        }}
                        className="text-destructive hover:text-destructive/80 transition-colors"
                        disabled={deleteMutation.isPending}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center py-8 text-muted-foreground">
            No API keys created yet. Generate one to get started.
          </p>
        )}
      </Card>
    </div>
  );
}

function Users({ onInvite }: { onInvite: () => void }) {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => authAPI.listUsers(),
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: 'admin' | 'user' }) =>
      authAPI.updateUserRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      alert('User role updated successfully');
    },
    onError: (err: Error) => {
      alert(`Failed to update role: ${err.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => authAPI.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      alert('User deleted successfully');
    },
    onError: (err: Error) => {
      alert(`Failed to delete user: ${err.message}`);
    },
  });

  return (
    <div className="space-y-6">
      <Card
        title="Team Members"
        actions={
          <Button size="sm" onClick={onInvite}>
            Invite User
          </Button>
        }
      >
        {isLoading ? (
          <p className="text-center py-8 text-muted-foreground">Loading...</p>
        ) : usersData && usersData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {usersData.map((user: User) => {
                  const isCurrentUser = user.sub === currentUser?.sub;
                  const canChangeRole = currentUser?.role === 'owner' && user.role !== 'owner';
                  return (
                    <tr key={user.sub} className={isCurrentUser ? 'bg-primary/5' : 'hover:bg-accent/50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                        {user.email}
                        {isCurrentUser && (
                          <span className="ml-2 text-xs text-primary font-medium">(You)</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {canChangeRole ? (
                          <select
                            value={user.role}
                            onChange={(e) =>
                              updateRoleMutation.mutate({
                                userId: user.sub,
                                role: e.target.value as 'admin' | 'user',
                              })
                            }
                            className="rounded-md border border-border-control bg-popover text-foreground text-xs px-2 py-1 focus:outline-none focus:border-ring focus:ring-2 focus:ring-ring/40 transition-colors"
                            disabled={updateRoleMutation.isPending}
                          >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                          </select>
                        ) : (
                          <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            user.role === 'owner'
                              ? 'bg-primary/20 text-primary'
                              : user.role === 'admin'
                              ? 'bg-info/10 text-info'
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {user.role}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        {!isCurrentUser && user.role !== 'owner' && currentUser?.role === 'owner' && (
                          <button
                            onClick={() => {
                              if (confirm(`Are you sure you want to remove ${user.email}?`)) {
                                deleteMutation.mutate(user.sub);
                              }
                            }}
                            className="text-destructive hover:text-destructive/80 transition-colors"
                            disabled={deleteMutation.isPending}
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center py-8 text-muted-foreground">No team members found</p>
        )}
      </Card>
    </div>
  );
}

function Invitations() {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'user'>('user');
  const queryClient = useQueryClient();

  const { data: invitationsData, isLoading } = useQuery({
    queryKey: ['invitations'],
    queryFn: () => authAPI.listInvitations(),
  });

  const createMutation = useMutation({
    mutationFn: () => authAPI.createInvitation(email, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      setEmail('');
      setRole('user');
      alert('Invitation sent successfully');
    },
    onError: (err: Error) => {
      alert(`Failed to send invitation: ${err.message}`);
    },
  });

  const resendMutation = useMutation({
    mutationFn: (invitationId: string) => authAPI.resendInvitation(invitationId),
    onSuccess: () => {
      alert('Invitation resent successfully');
    },
    onError: (err: Error) => {
      alert(`Failed to resend invitation: ${err.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (invitationId: string) => authAPI.deleteInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      alert('Invitation deleted successfully');
    },
    onError: (err: Error) => {
      alert(`Failed to delete invitation: ${err.message}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      createMutation.mutate();
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        {/* Send Invitation Form */}
        <Card title="Invite New User">
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <Input
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                required
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Role
                </label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value as 'admin' | 'user')}
                  className="block w-full rounded-md border border-border-control bg-popover px-3 py-2 text-sm text-foreground focus:outline-none focus:border-ring focus:ring-2 focus:ring-ring/40 focus:ring-offset-0 transition-colors"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <Button
                type="submit"
                isLoading={createMutation.isPending}
                disabled={!email.trim()}
                className="w-full"
              >
                Send Invitation
              </Button>
            </div>
          </form>
        </Card>

        {/* Pending Invitations */}
        <Card title="Pending Invitations">
          {isLoading ? (
            <p className="text-center py-8 text-muted-foreground">Loading...</p>
          ) : invitationsData && invitationsData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Invited By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Expires
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {invitationsData.map((invitation: Invitation) => {
                    const isExpired = new Date(invitation.expires_at) < new Date();
                    return (
                      <tr key={invitation.id} className="hover:bg-accent/50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                          {invitation.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            invitation.role === 'admin'
                              ? 'bg-info/10 text-info'
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {invitation.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            isExpired
                              ? 'bg-destructive/10 text-destructive'
                              : 'bg-warning/10 text-warning'
                          }`}>
                            {isExpired ? 'Expired' : 'Pending'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {invitation.invited_by_email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {new Date(invitation.expires_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-3">
                          {isExpired ? (
                            <button
                              onClick={() => resendMutation.mutate(invitation.id)}
                              className="text-primary hover:text-primary/80 transition-colors"
                              disabled={resendMutation.isPending}
                            >
                              Resend
                            </button>
                          ) : null}
                          <button
                            onClick={() => {
                              if (confirm('Are you sure you want to delete this invitation?')) {
                                deleteMutation.mutate(invitation.id);
                              }
                            }}
                            className="text-destructive hover:text-destructive/80 transition-colors"
                            disabled={deleteMutation.isPending}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center py-8 text-muted-foreground">No pending invitations</p>
          )}
        </Card>
      </div>

      <div>
        <Card title="About Invitations">
          <div className="text-sm text-muted-foreground space-y-3">
            <p>
              Invite team members to collaborate on your Varinym account.
            </p>
            <div className="pt-3 border-t border-border">
              <p className="font-medium text-card-foreground mb-2">Role Permissions:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li><strong>Admin:</strong> Full access to all features</li>
                <li><strong>User:</strong> Can view and create identities</li>
                <li><strong>Owner:</strong> Can manage users and roles</li>
              </ul>
            </div>
            <div className="pt-3 border-t border-border">
              <p className="font-medium text-card-foreground mb-2">Note:</p>
              <p className="text-xs">
                Invitations expire after 7 days. You can resend expired invitations.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
